import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Convert URL-safe base64 to Uint8Array (for VAPID key)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushPermissionPrompt = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    if (Notification.permission !== 'default') return;

    const dismissed = localStorage.getItem('push_prompt_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed);
      // Re-show after 7 days
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Wait a bit then show
    const t = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  const enable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setShow(false);
        return;
      }

      // Get VAPID public key
      const { data } = await api.get('/push/vapid-public-key');
      if (!data.configured || !data.publicKey) {
        toast.info('Notificações ativadas! (VAPID não configurado no servidor ainda)');
        localStorage.setItem('push_prompt_dismissed', Date.now().toString());
        setShow(false);
        return;
      }

      // Register service worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Subscribe
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      await api.post('/push/subscribe', subscription.toJSON());
      toast.success('Notificações ativadas!');
      localStorage.setItem('push_prompt_dismissed', Date.now().toString());
      setShow(false);
    } catch (e) {
      console.error('Push enable error:', e);
      toast.error('Não foi possível ativar notificações');
      setShow(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem('push_prompt_dismissed', Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed top-16 left-4 right-4 z-50 max-w-md mx-auto bg-white rounded-2xl shadow-xl border border-border p-4 animate-in slide-in-from-top">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-['Outfit'] font-bold">{t('push.enableTitle')}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t('push.enableSub')}</p>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="enable-push"
              onClick={enable}
              className="px-4 py-1.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold"
            >
              {t('push.enable')}
            </button>
            <button
              data-testid="dismiss-push"
              onClick={dismiss}
              className="px-4 py-1.5 bg-muted text-foreground rounded-full text-sm font-semibold"
            >
              {t('push.later')}
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="p-1 hover:bg-muted rounded-full">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

export default PushPermissionPrompt;
